/**
 * domains/ai/thesis2/evidence.ts — MER Algo Footprints v7.1H
 *
 * Normalizes raw engine outputs into a structured ThesisEvidenceInput.
 * This is the adapter layer — UI and pipeline code never touch raw engine
 * types directly; they go through this builder.
 *
 * No AI calls here. Pure deterministic transformation.
 */

import { SymbolContext }       from '../../market/services/buildSymbolContext';
import { RichSetup }            from '../../market/engines/setup2/types';
import { RiskAssessment, evaluateSetupRisk } from '../../risk/models/riskEngine';
import { OpportunityScore }    from '../../market/engines/ranking/rankingEngine';
import { getLiveGammaContext } from '../prompts/promptBuilder';
import { gammaToThesisLines }  from '../../market/engines/gamma/gammaContextMapper';
import { AppSettings }         from '../../../shared/types';
import { ThesisEvidenceInput, StructuredThesis } from './types';

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildThesisEvidence(
  ctx:          SymbolContext,
  settings:     AppSettings,
  ranking:      OpportunityScore | null,
  priorThesis:  StructuredThesis | null,
  aiConviction: number = 0,
  richSetup?:   RichSetup | null,
): ThesisEvidenceInput {
  const symbol = ctx.symbol;

  // Evaluate risk for the best setup (or null if no setup)
  const bestSetup = ctx.bestActionableSetup ?? ctx.topDisplaySetup ?? null;
  let risk: RiskAssessment | null = null;
  if (bestSetup) {
    try {
      risk = evaluateSetupRisk(bestSetup, ctx, aiConviction);
    } catch {
      risk = null;
    }
  }

  // Gamma context (optional — never blocks thesis)
  let gammaRead: string | null = null;
  try {
    const gammaCtx = getLiveGammaContext(symbol);
    if (gammaCtx) {
      gammaRead = gammaToThesisLines(gammaCtx) ?? null;
    }
  } catch {
    gammaRead = null;
  }

  return {
    symbol,
    timeframe:  ctx.interval ?? '5min',
    timestamp:  Date.now(),
    smc:        ctx.smc,
    regime:     ctx.regime,
    traps:      ctx.traps ?? [],
    setups:     ctx.setups ?? [],
    zones:      ctx.zones ?? [],
    risk,
    ranking,
    gammaRead,
    curPrice:   ctx.curPrice,
    curVWAP:    ctx.curVWAP ?? null,
    priorThesis,
    richSetup:  richSetup ?? null,
  };
}

// ─── Evidence summary (for prompt and fallback) ───────────────────────────────

export function summarizeEvidence(input: ThesisEvidenceInput): string {
  const lines: string[] = [];

  if (input.smc) {
    const smc = input.smc;
    lines.push(`SMC: bias=${smc.bias} strength=${smc.biasStrength}` +
      (smc.lastBOS   ? ` lastBOS=${smc.lastBOS.price?.toFixed(2)}@${smc.lastBOS.dir}`   : '') +
      (smc.lastCHOCH ? ` lastCHOCH=${smc.lastCHOCH.price?.toFixed(2)}@${smc.lastCHOCH.dir}` : ''));
  }

  if (input.regime) {
    const r = input.regime;
    lines.push(`Regime: ${r.label} (dominant=${r.dominant} conf=${r.confidence}% posture=${r.posture})`);
  }

  if (input.traps.length) {
    const top = input.traps[0];
    lines.push(`Traps: ${input.traps.length} active. Top: ${top.type} sev=${top.severity} side=${top.side}`);
  } else {
    lines.push('Traps: none active');
  }

  if (input.setups.length) {
    const s = input.setups[0];
    lines.push(`Setup: ${s.type} dir=${s.dir} conf=${s.confidence}%` +
      (s.entry ? ` entry="${s.entry}"` : '') +
      (s.invalidation ? ` inv="${s.invalidation}"` : '') +
      (s.t1 ? ` t1="${s.t1}"` : ''));
  } else {
    lines.push('Setup: no actionable setup identified');
  }

  if (input.zones.length) {
    const highSig = input.zones.filter(z => z.sig === 'High');
    lines.push(`Liquidity: ${input.zones.length} zones, ${highSig.length} high-significance`);
  }

  if (input.risk) {
    const r = input.risk;
    lines.push(`Risk: R/R=${r.expectedR?.toFixed(2) ?? 'N/A'} stopQ=${r.stopQuality} ` +
      `readiness=${r.readiness} trapConflict=${r.trapConflict} regimeConflict=${r.regimeConflict}`);
  }

  if (input.gammaRead) {
    lines.push(`Gamma: ${input.gammaRead}`);
  }

  lines.push(`Price: ${input.curPrice.toFixed(2)}` +
    (input.curVWAP ? ` VWAP=${input.curVWAP.toFixed(2)}` : ''));

  return lines.join('\n');
}

// ─── Normalized structured evidence payload ────────────────────────────────────
// Passed to the AI as a typed object rather than prose.
// AI reads fields directly — no ambiguity from free-form summaries.

export interface NormalizedEvidencePayload {
  // Direction
  directional_bias:    string;         // 'bullish' | 'bearish' | 'neutral' | 'mixed'
  trend_condition:     string;         // e.g. 'uptrend', 'ranging', 'reversal'

  // Structure
  structure_state:     string;         // e.g. 'BOS_bullish', 'CHOCH_bearish', 'none'
  structure_strength:  string;         // 'major' | 'minor' | 'unknown'
  last_bos:            string | null;  // e.g. 'bullish @ 142.50'
  last_choch:          string | null;

  // Liquidity
  liquidity_state:     string;         // e.g. '3 zones, 2 high-sig'
  nearest_zone:        string | null;  // e.g. 'support @ 140.20'
  likely_target:       string | null;

  // Traps
  trap_risk:           string;         // 'none' | 'Low' | 'Medium' | 'High'
  active_trap:         string | null;  // e.g. 'sweep_reversal @ 143.00'

  // Setup
  setup_type:          string;         // e.g. 'breakout_long'
  setup_ready:         boolean;
  setup_score:         number;         // 0–100
  setup_maturity:      string | null;  // 'developing' | 'confirmed' | 'actionable'

  // Risk
  risk_reward_estimate: number | null; // from riskEngine
  stop_quality:         number;        // 0–100
  invalidation_hint:   string | null;  // from setup engine

  // Regime
  regime_label:        string;         // e.g. 'Mean Reversion'
  regime_dominant:     string;         // e.g. 'meanReversion'
  regime_confidence:   number;         // 0–100

  // Gamma
  gamma_available:     boolean;
  gamma_context:       string | null;

  // Confluence / conflict flags
  confluence_flags:    string[];       // things that agree
  conflict_flags:      string[];       // things that disagree

  // Price context
  current_price:       number;
  vwap:                number | null;
  vwap_position:       string | null;  // 'above' | 'below' | null

  // Gate summary (sent to model explicitly)
  gates_passed:              boolean;
  gate_fail_reasons:         string[];
  can_be_actionable:         boolean;
  // setup2 evaluation metadata
  evaluated_no_valid_setup:  boolean;  // true = evaluation ran, no valid setup found
  setup_state:               string | null;
  setup_readiness_score:     number | null;
  setup_disqualifiers:       string[];
}

export function buildNormalizedPayload(input: ThesisEvidenceInput): NormalizedEvidencePayload {
  const { smc, regime, traps, setups, zones, risk, gammaRead, curPrice, curVWAP } = input;

  // Direction
  const directional_bias = smc?.bias === 'bullish' ? 'bullish'
    : smc?.bias === 'bearish' ? 'bearish' : 'neutral';

  const regimeDom = regime?.dominant ?? 'unknown';
  const trend_condition =
    regimeDom === 'trend'         ? 'trend'
    : regimeDom === 'breakout'    ? 'breakout'
    : regimeDom === 'meanReversion'? 'mean_reversion'
    : regimeDom === 'noTrade'     ? 'no_trade'
    : 'ranging';

  // Structure
  const structure_state = smc?.lastCHOCH
    ? `CHOCH_${smc.lastCHOCH.dir}`
    : smc?.lastBOS
      ? `BOS_${smc.lastBOS.dir}`
      : 'none';
  const last_bos   = smc?.lastBOS   ? `${smc.lastBOS.dir} @ ${smc.lastBOS.price?.toFixed(2) ?? '?'}`   : null;
  const last_choch = smc?.lastCHOCH ? `${smc.lastCHOCH.dir} @ ${smc.lastCHOCH.price?.toFixed(2) ?? '?'}` : null;

  // Liquidity
  const highSig   = zones.filter(z => z.sig === 'High');
  const liquidity_state = zones.length
    ? `${zones.length} zones, ${highSig.length} high-sig`
    : 'none';
  const nearest_zone = highSig[0] ? `${highSig[0].type} @ ${highSig[0].price.toFixed(2)}` : null;

  // Traps
  const topTrap = traps[0] ?? null;
  const trap_risk  = topTrap?.severity ?? 'none';
  const active_trap = topTrap ? `${topTrap.type} @ sev=${topTrap.severity}` : null;

  // Setup
  const richSup    = (input as any).richSetup as RichSetup | null ?? null;
  const topSetup   = setups[0] ?? null;
  // Prefer setup2 RichSetup fields when available — more precise
  const setup_type       = (richSup && richSup.setup_classification !== 'NO_VALID_SETUP')
    ? richSup.setup_classification
    : topSetup?.type ?? 'none';
  const setup_score      = (richSup && richSup.setup_classification !== 'NO_VALID_SETUP')
    ? richSup.setup_score
    : topSetup?.confidence ?? 0;
  const setup_maturity   = richSup && richSup.setup_classification !== 'NO_VALID_SETUP'
    ? (richSup.setup_state === 'READY' || richSup.setup_state === 'ACTIVE' ? 'actionable'
       : richSup.setup_state === 'DEVELOPING' ? 'confirmed' : 'developing')
    : (topSetup as any)?.maturity ?? null;
  const invalidation_hint = richSup?.risk_model.invalidation_event ?? topSetup?.invalidation ?? null;

  // ── setup_ready — authoritative from setup2; strict legacy fallback ─────────
  const isNoValidSetup = richSup?.setup_classification === 'NO_VALID_SETUP';
  const legacyReady    = (topSetup?.confidence ?? 0) >= 60 && (topSetup as any)?.maturity === 'actionable';
  const setup_ready    = isNoValidSetup
    ? false   // explicit evaluated no-trade outcome — NOT missing data
    : richSup
      ? richSup.actionability.is_actionable &&
        (richSup.setup_state === 'READY' || richSup.setup_state === 'ACTIVE') &&
        richSup.readiness_score >= 65
      : legacyReady;

  // Risk
  const risk_reward_estimate = risk?.expectedR ?? null;
  const stop_quality         = risk?.stopQuality ?? 0;

  // Confluence / conflict
  const confluence_flags: string[] = [];
  const conflict_flags:   string[] = [];
  if (!risk?.trapConflict)   confluence_flags.push('no trap conflict');
  else                       conflict_flags.push('trap conflict active');
  if (!risk?.regimeConflict) confluence_flags.push('regime aligned');
  else                       conflict_flags.push('regime conflict active');
  if (topSetup && smc) {
    const smcDir = smc.bias === 'bullish' ? 'long' : 'short';
    if (topSetup.dir === smcDir) confluence_flags.push('setup/SMC direction aligned');
    else                        conflict_flags.push('setup/SMC direction conflict');
  }

  // VWAP position
  const vwap_position = curVWAP
    ? curPrice >= curVWAP ? 'above' : 'below'
    : null;

  return {
    directional_bias,
    trend_condition,
    structure_state,
    structure_strength: smc?.biasStrength ?? 'unknown',
    last_bos,
    last_choch,
    liquidity_state,
    nearest_zone,
    likely_target: (() => {
      // Derive likely target from highest-significance zone in direction of bias.
      // Bullish: nearest high-sig resistance (price above = target).
      // Bearish: nearest high-sig support (price below = target).
      // Falls back to any zone if no high-sig found.
      const dir = directional_bias;
      if (dir === 'bullish') {
        const t = highSig.find(z => z.type === 'resistance') ?? zones.find(z => z.type === 'resistance');
        return t ? `${t.type} @ ${t.price.toFixed(2)}` : null;
      }
      if (dir === 'bearish') {
        const t = highSig.find(z => z.type === 'support') ?? zones.find(z => z.type === 'support');
        return t ? `${t.type} @ ${t.price.toFixed(2)}` : null;
      }
      return null;
    })(),
    trap_risk,
    active_trap,
    setup_type,
    setup_ready,   // derived from setup2 semantics — see above
    setup_score,
    setup_maturity,
    risk_reward_estimate,
    stop_quality,
    invalidation_hint,
    regime_label:      regime?.label       ?? 'unknown',
    regime_dominant:   regime?.dominant    ?? 'unknown',
    regime_confidence: regime?.confidence  ?? 0,
    gamma_available:   !!gammaRead,
    gamma_context:     gammaRead,
    confluence_flags,
    conflict_flags,
    current_price: curPrice,
    vwap:          curVWAP ?? null,
    vwap_position,
    // Gates — populated by pipeline before passing to prompt builder
    gates_passed:           false,
    gate_fail_reasons:      [],
    can_be_actionable:      false,
    // setup2 evaluation metadata — let AI understand setup state quality
    evaluated_no_valid_setup: isNoValidSetup,   // true = evaluated, no trade, explicit outcome
    setup_state:              richSup?.setup_state ?? null,
    setup_readiness_score:    richSup?.readiness_score ?? null,
    setup_disqualifiers:      richSup?.disqualifiers.filter(d => d.fatal).map(d => d.rule) ?? [],
  };
}
